import { useEffect, useMemo, useRef, useState } from "react"
import { nachfragen } from "@/components/portal/Rueckfrage"
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import { FilterBar } from "@/components/portal/FilterBar"
import { toast } from "@/components/portal/Toast"
import { useTenantFilter } from "@/hooks/useTenantFilter"
import { CallTaskModal } from "@/components/portal/CallTaskModal"
import { parseServerDate } from "@/lib/utils"

/**
 * WhatsApp-Posteingang (Paul 28.09.2026: „die Chatverläufe sehen und über
 * WhatsApp mit Kunden kommunizieren").
 *
 * Alle Betriebe teilen EINE WhatsApp-Nummer. Eine Unterhaltung ist deshalb
 * Kundennummer + Betrieb; schreibt ein Kunde von sich aus, steht sie „ohne
 * Betrieb" da, bis das Büro sie zuordnet.
 *
 * Meta erlaubt Freitext nur 24 Stunden nach der letzten Kundennachricht —
 * danach bietet die Seite die freigegebene Rückfrage an, die das Gespräch
 * wieder öffnet, sobald der Kunde antwortet.
 */

export type Unterhaltung = {
  telefon: string; anzeige: string; kontakt_name: string | null; tenant_slug: string
  letzter_text: string; letzte_richtung: "ein" | "aus"; letzter_status: string | null; letzte_at: string | null
  ungelesen: number; fenster_offen: boolean; fenster_offen_bis: string | null
  art?: "kunde" | "intern"; zuordnung?: string | null
}
export type Medium = {
  typ: "image" | "audio" | "video" | "document" | "sticker"; mime: string | null; name: string | null
  groesse: number | null; transkript: string | null; url: string | null
}
type Nachricht = {
  id: number; richtung: "ein" | "aus"; text: string; vorlage: string | null; status: string | null
  fehler: string | null; sms_rueckfall: boolean; benutzer: string | null; at: string | null
  medium?: Medium | null
}
/** Aufgaben-Vorschlag aus einer Inhaber-Nachricht (Paul 08.10.2026: „Vorschlag + Klick"). */
export type Vorschlag = {
  id: number; nachricht_id: number; status: "offen" | "doppelt" | "in_arbeit" | "angelegt" | "verworfen" | "kein_vorschlag"
  titel: string | null; beschreibung: string | null; faellig: string | null
  hero_contact_id: number | null; kontakt_name: string | null
  project_match_id: number | null; projekt_name: string | null
  begruendung: string | null; doppelt_titel: string | null; benutzer: string | null
}
type Verlauf = {
  nachrichten: Nachricht[]; fenster_offen: boolean; fenster_offen_bis: string | null; vorschlaege?: Vorschlag[]
}

const OHNE_BETRIEB = "-"

async function holeJson<T>(url: string, init?: RequestInit): Promise<T> {
  const r = await fetch(url, { credentials: "include", ...init })
  const daten = await r.json().catch(() => ({}))
  if (!r.ok) throw new Error((daten as { detail?: string }).detail || `HTTP ${r.status}`)
  return daten as T
}

function zeit(iso: string | null): string {
  const d = parseServerDate(iso)
  if (!d) return ""
  const heute = new Date().toDateString() === d.toDateString()
  return heute
    ? d.toLocaleTimeString("de-DE", { hour: "2-digit", minute: "2-digit" })
    : d.toLocaleDateString("de-DE", { day: "2-digit", month: "2-digit" }) + " " +
      d.toLocaleTimeString("de-DE", { hour: "2-digit", minute: "2-digit" })
}

/** Häkchen wie in WhatsApp: gesendet ✓, zugestellt ✓✓, gelesen ✓✓ blau. */
export function Haekchen({ n }: { n: Nachricht }) {
  if (n.status === "failed") {
    return <span title={n.fehler || "nicht zustellbar"} style={{ color: "#b42318" }}>
      nicht zugestellt{n.sms_rueckfall ? " · per SMS gesendet" : ""}
    </span>
  }
  const text = n.status === "read" || n.status === "delivered" ? "✓✓" : "✓"
  const titel = n.status === "read" ? "gelesen" : n.status === "delivered" ? "zugestellt" : "gesendet"
  return <span title={titel} style={{ color: n.status === "read" ? "#1d9bf0" : "#888780" }}>{text}</span>
}

function Liste({ items, aktiv, onWahl, namen }: {
  items: Unterhaltung[]; aktiv: string | null; onWahl: (u: Unterhaltung) => void; namen: Record<string, string>
}) {
  if (!items.length) {
    return <div style={{ padding: 24, color: "var(--text-muted)", fontSize: 13 }}>Noch keine WhatsApp-Nachrichten.</div>
  }
  return (
    <div style={{ overflowY: "auto", flex: 1 }}>
      {items.map((u) => {
        const schluessel = `${u.telefon}|${u.tenant_slug}`
        return (
          <button key={schluessel} type="button" onClick={() => onWahl(u)} data-testid={`wa-unterhaltung-${u.telefon}`}
            style={{
              display: "block", width: "100%", textAlign: "left", padding: "10px 14px", border: 0, cursor: "pointer",
              borderBottom: "0.5px solid rgba(44,44,42,0.08)",
              background: aktiv === schluessel ? "rgba(37,211,102,0.08)" : "transparent",
            }}>
            <div style={{ display: "flex", justifyContent: "space-between", gap: 8 }}>
              <strong style={{ fontSize: 13 }}>{u.kontakt_name || u.anzeige}</strong>
              <span style={{ fontSize: 11, color: "#888780", whiteSpace: "nowrap" }}>{zeit(u.letzte_at)}</span>
            </div>
            <div style={{ fontSize: 11, color: "#888780" }}>
              {u.art === "intern" && <span className="pp-badge pp-badge-new" style={{ marginRight: 4 }} data-testid="wa-intern">Intern</span>}
              {u.tenant_slug === OHNE_BETRIEB ? "ohne Betrieb" : (namen[u.tenant_slug] || u.tenant_slug)}
              {u.kontakt_name ? ` · ${u.anzeige}` : ""}
            </div>
            <div style={{ display: "flex", justifyContent: "space-between", gap: 8, marginTop: 2 }}>
              <span style={{ fontSize: 12, color: "#5f5e5a", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                {u.letzte_richtung === "aus" ? "Sie: " : ""}{u.letzter_text}
              </span>
              {u.ungelesen > 0 && <span className="pp-badge pp-badge-success" data-testid="wa-ungelesen">{u.ungelesen}</span>}
            </div>
          </button>
        )
      })}
    </div>
  )
}

/** Anhang in der Sprechblase (Paul 08.10.2026: „bilder … und sprachnachricht"). */
export function Anhang({ m }: { m: Medium }) {
  if (!m.url) {
    return <div style={{ fontSize: 12, color: "#888780" }} data-testid="wa-anhang-laedt">
      {m.typ === "audio" ? "🎤 Sprachnachricht" : m.typ === "image" ? "📷 Bild" : "📎 Anhang"} wird geladen …
    </div>
  }
  return (
    <div style={{ marginBottom: 4 }} data-testid={`wa-anhang-${m.typ}`}>
      {(m.typ === "image" || m.typ === "sticker") && (
        <a href={m.url} target="_blank" rel="noreferrer">
          <img src={m.url} alt={m.name || "Bild"} style={{ maxWidth: 260, maxHeight: 260, borderRadius: 6, display: "block" }} />
        </a>
      )}
      {m.typ === "audio" && <audio controls preload="none" src={m.url} style={{ width: 240 }} />}
      {m.typ === "video" && <video controls preload="none" src={m.url} style={{ maxWidth: 260, borderRadius: 6 }} />}
      {m.typ === "document" && (
        <a href={m.url} target="_blank" rel="noreferrer">📄 {m.name || "Dokument"}</a>
      )}
      {m.typ === "audio" && m.transkript && (
        <div style={{ fontSize: 12, color: "#5f5e5a", marginTop: 4, fontStyle: "italic" }} data-testid="wa-transkript">
          „{m.transkript}“
        </div>
      )}
    </div>
  )
}

/** Sprachaufnahme im Browser. Liefert eine Datei (WebM/MP4/Ogg) — der Server
 *  wandelt sie in Ogg/Opus um, das WhatsApp als Sprachnachricht abspielt. */
function useAufnahme(fertig: (datei: File) => void) {
  const rec = useRef<MediaRecorder | null>(null)
  const [laeuft, setLaeuft] = useState(false)
  const start = async () => {
    try {
      const strom = await navigator.mediaDevices.getUserMedia({ audio: true })
      const typ = ["audio/ogg;codecs=opus", "audio/webm;codecs=opus", "audio/mp4"]
        .find((t) => typeof MediaRecorder !== "undefined" && MediaRecorder.isTypeSupported?.(t)) || ""
      const r = new MediaRecorder(strom, typ ? { mimeType: typ } : undefined)
      const teile: Blob[] = []
      r.ondataavailable = (e) => { if (e.data.size) teile.push(e.data) }
      r.onstop = () => {
        strom.getTracks().forEach((t) => t.stop())
        const mime = (r.mimeType || typ || "audio/webm").split(";")[0]
        const endung = mime.includes("ogg") ? "ogg" : mime.includes("mp4") ? "m4a" : "webm"
        fertig(new File([new Blob(teile, { type: mime })], `aufnahme.${endung}`, { type: mime }))
      }
      r.start()
      rec.current = r
      setLaeuft(true)
    } catch {
      toast("Mikrofon nicht verfügbar — bitte im Browser erlauben.", "error")
    }
  }
  const stopp = () => { rec.current?.stop(); rec.current = null; setLaeuft(false) }
  return { laeuft, start, stopp }
}

/** Inhaber von sich aus anschreiben (Paul 08.10.2026). Inhaber = Einträge unter
 *  „Team-Rufnummern" des Betriebs. Nach 24 Stunden ohne Nachricht des
 *  Inhabers geht der Text automatisch über die freigegebene Vorlage. */
type Inhaber = { tenant_slug: string; betrieb: string; name: string; telefon: string; anzeige: string }
export function InhaberAnschreiben({ onGesendet, onClose }: {
  onGesendet: (telefon: string, tenant: string) => void; onClose: () => void
}) {
  const liste = useQuery<{ inhaber: Inhaber[] }>({
    queryKey: ["whatsapp-inhaber"], queryFn: () => holeJson("/api/v1/whatsapp/inhaber"),
  })
  const [wahl, setWahl] = useState("")
  const [text, setText] = useState("")
  const senden = useMutation({
    mutationFn: () => {
      const [tenant, telefon] = wahl.split("|")
      return holeJson<{ als_vorlage: boolean; telefon: string }>("/api/v1/whatsapp/inhaber/nachricht", {
        method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ tenant, telefon, text }),
      }).then((d) => ({ ...d, tenant }))
    },
    onSuccess: (d) => {
      toast(d.als_vorlage ? "Gesendet (als Vorlage — der Inhaber hat seit 24 h nicht geschrieben)" : "Gesendet")
      onGesendet(d.telefon, d.tenant)
    },
    onError: (e: Error) => toast(e.message, "error"),
  })
  const eintraege = liste.data?.inhaber || []
  return (
    <div className="pp-overlay" data-portal-modal style={{ position: "fixed", inset: 0, zIndex: 1000, background: "rgba(0,0,0,0.5)",
      display: "flex", alignItems: "center", justifyContent: "center", padding: 16 }}
      onMouseDown={(e) => { if (e.target === e.currentTarget) onClose() }}>
      <form className="pp-card" style={{ width: "100%", maxWidth: 480, padding: 18 }} data-testid="wa-inhaber-dialog"
        onSubmit={(e) => { e.preventDefault(); if (wahl && text.trim()) senden.mutate() }}>
        <h2 style={{ fontSize: 15, fontWeight: 600, marginBottom: 10 }}>Inhaber / Team anschreiben</h2>
        {liste.isSuccess && !eintraege.length && (
          <div style={{ fontSize: 13, color: "#5f5e5a", marginBottom: 10 }}>
            Noch niemand hinterlegt — in der Betriebs-Konfiguration unter „Team-Rufnummern (Inhaber, Mitarbeiter)“ eintragen.
          </div>
        )}
        <label className="pp-form-label">An</label>
        <select className="pp-input" value={wahl} onChange={(e) => setWahl(e.target.value)} data-testid="wa-inhaber-wahl">
          <option value="">Person wählen …</option>
          {eintraege.map((i) => (
            <option key={`${i.tenant_slug}|${i.telefon}`} value={`${i.tenant_slug}|${i.telefon}`}>
              {i.name} · {i.betrieb} · {i.anzeige}
            </option>
          ))}
        </select>
        <label className="pp-form-label" style={{ marginTop: 10 }}>Nachricht</label>
        <textarea className="pp-input" rows={4} maxLength={700} value={text} onChange={(e) => setText(e.target.value)}
          placeholder="Was braucht ihr vom Inhaber?" data-testid="wa-inhaber-text" />
        <div style={{ fontSize: 11, color: "#888780", marginTop: 4 }}>
          Hat der Inhaber seit 24 Stunden nicht geschrieben, geht der Text über die freigegebene Vorlage
          („Hallo …, hier ist das werkXpartner-Büro zu …“).
        </div>
        <div style={{ display: "flex", justifyContent: "flex-end", gap: 8, marginTop: 12 }}>
          <button type="button" className="pp-btn" onClick={onClose}>Abbrechen</button>
          <button type="submit" className="pp-btn pp-btn-primary" disabled={!wahl || !text.trim() || senden.isPending}
            data-testid="wa-inhaber-senden">Senden</button>
        </div>
      </form>
    </div>
  )
}

/** Karte unter der Inhaber-Nachricht. Angelegt wird nur per Klick — die KI
 *  schlägt vor, ein Mensch entscheidet (Paul 29.09.2026: keine Aufgaben automatisch). */
export function VorschlagKarte({ v, onAnlegen, onVerwerfen, beschaeftigt }: {
  v: Vorschlag; onAnlegen: () => void; onVerwerfen: () => void; beschaeftigt: boolean
}) {
  if (v.status === "angelegt") {
    return <div style={{ fontSize: 11, color: "#3b6d11", margin: "0 0 8px 4px" }} data-testid="wa-vorschlag-angelegt">
      ✓ Aufgabe angelegt{v.benutzer ? ` von ${v.benutzer}` : ""}: {v.titel}
    </div>
  }
  if (v.status === "kein_vorschlag") {
    // Paul 08.10.2026: „wo sehe ich jetzt dass eine aufgabe vorgeschlagen wird?" —
    // auch das Nein der KI ist sichtbar, und das Büro kann trotzdem anlegen.
    return <div style={{ fontSize: 11, color: "#888780", margin: "0 0 8px 4px", maxWidth: "75%" }}
      data-testid="wa-vorschlag-kein_vorschlag">
      🤖 Keine Aufgabe erkannt{v.begruendung ? `: ${v.begruendung}` : ""}{" "}
      <button type="button" className="pp-btn pp-btn-sm" style={{ marginLeft: 4 }} disabled={beschaeftigt}
        onClick={onAnlegen} data-testid="wa-vorschlag-doch-anlegen">Doch als Aufgabe anlegen …</button>
    </div>
  }
  if (v.status === "verworfen") {
    return <div style={{ fontSize: 11, color: "#888780", margin: "0 0 8px 4px" }} data-testid="wa-vorschlag-verworfen">
      Aufgaben-Vorschlag verworfen{v.benutzer ? ` von ${v.benutzer}` : ""}
    </div>
  }
  const doppelt = v.status === "doppelt"
  return (
    <div data-testid={`wa-vorschlag-${v.status}`} style={{
      maxWidth: "75%", margin: "0 0 10px 0", padding: "8px 10px", borderRadius: 8, fontSize: 12,
      background: doppelt ? "#fff8e6" : "#eef4ff", border: `0.5px solid ${doppelt ? "#e8c46a" : "#9db7e8"}`,
    }}>
      <div style={{ fontWeight: 600, marginBottom: 2 }}>
        {doppelt ? "Ähnliche Aufgabe gibt es schon" : "Aufgabe vorgeschlagen"}
      </div>
      <div style={{ fontSize: 13 }}>{v.titel}</div>
      {v.beschreibung && <div style={{ color: "#5f5e5a", marginTop: 2 }}>{v.beschreibung}</div>}
      <div style={{ color: "#5f5e5a", marginTop: 4 }}>
        {[v.kontakt_name && `Kunde: ${v.kontakt_name}${v.hero_contact_id ? "" : " (nicht eindeutig — bitte wählen)"}`,
          v.projekt_name && `Projekt: ${v.projekt_name}`,
          v.faellig && `Fällig: ${v.faellig.slice(8, 10)}.${v.faellig.slice(5, 7)}.${v.faellig.slice(0, 4)}`,
        ].filter(Boolean).join(" · ")}
      </div>
      {doppelt && v.doppelt_titel && <div style={{ marginTop: 4 }} data-testid="wa-vorschlag-doppelt-titel">
        Offen: „{v.doppelt_titel}“
      </div>}
      <div style={{ display: "flex", gap: 6, marginTop: 6 }}>
        <button type="button" className="pp-btn pp-btn-sm pp-btn-primary" disabled={beschaeftigt || v.status === "in_arbeit"}
          onClick={onAnlegen} data-testid="wa-vorschlag-anlegen">
          {doppelt ? "Trotzdem anlegen …" : "Prüfen & anlegen …"}
        </button>
        <button type="button" className="pp-btn pp-btn-sm" disabled={beschaeftigt || v.status === "in_arbeit"}
          onClick={onVerwerfen} data-testid="wa-vorschlag-verwerfen">
          Verwerfen
        </button>
      </div>
    </div>
  )
}

function Chat({ u, namen }: { u: Unterhaltung; namen: Record<string, string> }) {
  const qc = useQueryClient()
  const [text, setText] = useState("")
  const [zuordnen, setZuordnen] = useState("")
  const ende = useRef<HTMLDivElement>(null)
  const pfad = `/api/v1/whatsapp/unterhaltungen/${encodeURIComponent(u.telefon)}`
  const q = `?tenant=${encodeURIComponent(u.tenant_slug)}`
  const verlauf = useQuery<Verlauf>({
    queryKey: ["whatsapp-verlauf", u.telefon, u.tenant_slug],
    queryFn: () => holeJson<Verlauf>(pfad + q),
    refetchInterval: 15_000,
  })

  // Beim Öffnen gelesen markieren — die Zahl in der Liste verschwindet.
  useEffect(() => {
    if (u.ungelesen > 0) {
      holeJson(pfad + "/gelesen" + q, { method: "POST" })
        .then(() => qc.invalidateQueries({ queryKey: ["whatsapp-unterhaltungen"] }))
        .catch(() => { /* egal, nächster Aufruf markiert */ })
    }
  }, [u.telefon, u.tenant_slug, u.ungelesen]) // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { ende.current?.scrollIntoView?.({ block: "end" }) }, [verlauf.data?.nachrichten.length])

  const neu = () => {
    qc.invalidateQueries({ queryKey: ["whatsapp-verlauf", u.telefon, u.tenant_slug] })
    qc.invalidateQueries({ queryKey: ["whatsapp-unterhaltungen"] })
  }
  const senden = useMutation({
    mutationFn: () => holeJson(pfad + "/nachricht" + q, {
      method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ text }),
    }),
    onSuccess: () => { setText(""); neu() },
    onError: (e: Error) => toast(e.message, "error"),
  })
  const rueckfrage = useMutation({
    mutationFn: () => holeJson(pfad + "/rueckfrage" + q, { method: "POST" }),
    onSuccess: () => { toast("Rückfrage gesendet — sobald der Kunde antwortet, können Sie frei schreiben."); neu() },
    onError: (e: Error) => toast(e.message, "error"),
  })
  const anhang = useMutation({
    mutationFn: ({ datei, sprache }: { datei: File; sprache: boolean }) => {
      const fd = new FormData()
      fd.append("datei", datei)
      fd.append("beschriftung", sprache ? "" : text)
      fd.append("sprachnachricht", sprache ? "true" : "false")
      return holeJson(pfad + "/medium" + q, { method: "POST", body: fd })
    },
    onSuccess: (_d, v) => { if (!v.sprache) setText(""); neu() },
    onError: (e: Error) => toast(e.message, "error"),
  })
  const aufnahme = useAufnahme((datei) => anhang.mutate({ datei, sprache: true }))
  const dateiWahl = useRef<HTMLInputElement>(null)
  const zuordnung = useMutation({
    mutationFn: () => holeJson(pfad + "/zuordnen", {
      method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ tenant: zuordnen }),
    }),
    onSuccess: () => { toast("Zugeordnet"); qc.invalidateQueries({ queryKey: ["whatsapp-unterhaltungen"] }) },
    onError: (e: Error) => toast(e.message, "error"),
  })

  const [anlegen, setAnlegen] = useState<Vorschlag | null>(null)
  const verwerfen = useMutation({
    mutationFn: (id: number) => holeJson(`/api/v1/whatsapp/vorschlaege/${id}/verwerfen`, {
      method: "POST", headers: { "Content-Type": "application/json" }, body: "{}",
    }),
    onSuccess: () => { toast("Vorschlag verworfen"); neu() },
    onError: (e: Error) => toast(e.message, "error"),
  })

  const d = verlauf.data
  const vorschlagZu = useMemo(() => {
    const m: Record<number, Vorschlag[]> = {}
    for (const v of d?.vorschlaege || []) (m[v.nachricht_id] ||= []).push(v)
    return m
  }, [d?.vorschlaege])
  const ohneBetrieb = u.tenant_slug === OHNE_BETRIEB
  return (
    <div className="pp-card" style={{ padding: 0, display: "flex", flexDirection: "column", height: "100%" }} data-testid="wa-chat">
      <div style={{ padding: "10px 16px", borderBottom: "0.5px solid rgba(44,44,42,0.08)" }}>
        <strong style={{ fontSize: 14 }}>{u.kontakt_name || u.anzeige}</strong>
        <div style={{ fontSize: 12, color: "#888780" }}>
          {u.anzeige} · {ohneBetrieb ? "ohne Betrieb" : (namen[u.tenant_slug] || u.tenant_slug)}
          {u.art === "intern" ? " · Inhaber/Team" : ""}
          {u.zuordnung ? <span data-testid="wa-zuordnung-hinweis"> · {u.zuordnung}</span> : null}
        </div>
      </div>
      <div style={{ flex: 1, overflowY: "auto", padding: 16, background: "#f4f1ea" }}>
        {verlauf.isLoading && <div style={{ color: "#888780", fontSize: 13 }}>Lädt …</div>}
        {d?.nachrichten.map((n) => (
          <div key={n.id}>
          <div style={{ display: "flex", justifyContent: n.richtung === "aus" ? "flex-end" : "flex-start", marginBottom: 8 }}>
            <div data-testid={`wa-nachricht-${n.richtung}`} style={{
              maxWidth: "75%", padding: "8px 10px", borderRadius: 8, fontSize: 13, whiteSpace: "pre-wrap", wordBreak: "break-word",
              background: n.richtung === "aus" ? "#d9fdd3" : "#fff", boxShadow: "0 1px 0.5px rgba(0,0,0,0.08)",
            }}>
              {n.vorlage && <div style={{ fontSize: 10, color: "#888780", marginBottom: 2 }}>Vorlage</div>}
              {n.medium && <Anhang m={n.medium} />}
              {n.text}
              <div style={{ fontSize: 10, color: "#888780", textAlign: "right", marginTop: 4, display: "flex", gap: 6, justifyContent: "flex-end" }}>
                {n.benutzer && <span>{n.benutzer}</span>}
                <span>{zeit(n.at)}</span>
                {n.richtung === "aus" && <Haekchen n={n} />}
              </div>
            </div>
          </div>
          {(vorschlagZu[n.id] || []).map((v) => (
            <VorschlagKarte key={v.id} v={v} beschaeftigt={verwerfen.isPending}
              onAnlegen={() => setAnlegen(v)} onVerwerfen={() => verwerfen.mutate(v.id)} />
          ))}
          </div>
        ))}
        <div ref={ende} />
      </div>
      <div style={{ padding: 12, borderTop: "0.5px solid rgba(44,44,42,0.08)" }}>
        {ohneBetrieb ? (
          <div style={{ display: "flex", gap: 8, alignItems: "center", fontSize: 13 }} data-testid="wa-zuordnen">
            <span>Dieser Kunde hat von sich aus geschrieben. Welchem Betrieb gehört die Unterhaltung?</span>
            <select className="pp-input" value={zuordnen} onChange={(e) => setZuordnen(e.target.value)} style={{ width: "auto" }}>
              <option value="">Betrieb wählen …</option>
              {Object.entries(namen).map(([slug, name]) => <option key={slug} value={slug}>{name}</option>)}
            </select>
            <button className="pp-btn pp-btn-sm pp-btn-primary" disabled={!zuordnen || zuordnung.isPending} onClick={() => zuordnung.mutate()}>
              Zuordnen
            </button>
          </div>
        ) : d && !d.fenster_offen && u.art !== "intern" ? (
          <div style={{ display: "flex", gap: 8, alignItems: "center", fontSize: 13 }} data-testid="wa-fenster-zu">
            <span style={{ flex: 1, color: "#5f5e5a" }}>
              Der Kunde hat seit über 24 Stunden nicht geschrieben. WhatsApp erlaubt dann nur eine freigegebene Vorlage.
            </span>
            <button className="pp-btn pp-btn-sm pp-btn-primary" disabled={rueckfrage.isPending} onClick={() => rueckfrage.mutate()}>
              Rückfrage senden
            </button>
          </div>
        ) : (
          <form style={{ display: "flex", gap: 8, alignItems: "flex-end" }}
            onSubmit={(e) => { e.preventDefault(); if (text.trim()) senden.mutate() }}>
            <textarea className="pp-input" rows={2} value={text} onChange={(e) => setText(e.target.value)}
              placeholder={u.art === "intern" ? "Nachricht an den Inhaber …" : "Nachricht an den Kunden …"}
              style={{ flex: 1, resize: "vertical" }} data-testid="wa-eingabe"
              onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); if (text.trim()) senden.mutate() } }} />
            {d?.fenster_offen && (
              <>
                <input ref={dateiWahl} type="file" hidden accept="image/jpeg,image/png,application/pdf,audio/*,video/mp4"
                  data-testid="wa-datei" onChange={(e) => {
                    const f = e.target.files?.[0]; e.target.value = ""
                    if (f) anhang.mutate({ datei: f, sprache: false })
                  }} />
                <button type="button" className="pp-btn" title="Bild oder Datei senden (Text im Feld wird Beschriftung)"
                  disabled={anhang.isPending} onClick={() => dateiWahl.current?.click()} data-testid="wa-anhang-knopf">📎</button>
                <button type="button" className="pp-btn" data-testid="wa-aufnahme-knopf"
                  title={aufnahme.laeuft ? "Aufnahme beenden und senden" : "Sprachnachricht aufnehmen"}
                  disabled={anhang.isPending} onClick={() => aufnahme.laeuft ? aufnahme.stopp() : aufnahme.start()}
                  style={aufnahme.laeuft ? { background: "#fde2e1", color: "#b42318" } : undefined}>
                  {aufnahme.laeuft ? "⏹" : "🎤"}
                </button>
              </>
            )}
            <button type="submit" className="pp-btn pp-btn-primary" disabled={!text.trim() || senden.isPending}>Senden</button>
          </form>
        )}
        {d?.fenster_offen && d.fenster_offen_bis && (
          <div style={{ fontSize: 11, color: "#888780", marginTop: 4 }}>Freies Schreiben möglich bis {zeit(d.fenster_offen_bis)}</div>
        )}
        {d && !d.fenster_offen && u.art === "intern" && (
          <div style={{ fontSize: 11, color: "#888780", marginTop: 4 }} data-testid="wa-inhaber-vorlage-hinweis">
            Der Inhaber hat seit über 24 Stunden nicht geschrieben — der Text geht über die freigegebene Vorlage.
            Bilder und Sprachnachrichten wieder, sobald er antwortet.
          </div>
        )}
        {anhang.isPending && <div style={{ fontSize: 11, color: "#888780", marginTop: 4 }}>Anhang wird gesendet …</div>}
      </div>
      <CallTaskModal open={!!anlegen} onClose={() => { setAnlegen(null); neu() }} mode="whatsapp"
        callId={anlegen?.id ?? 0} tenantSlug={u.tenant_slug}
        defaultTitle={anlegen?.titel || ""} defaultComment={anlegen?.beschreibung || ""}
        defaultContactId={anlegen?.hero_contact_id ?? null} defaultContactName={anlegen?.kontakt_name || ""}
        defaultDueDate={anlegen?.faellig ?? null} defaultProjectId={anlegen?.project_match_id ?? null} />
    </div>
  )
}

type Punkt = { schluessel: string; label: string; ok: boolean | null; wert: string; hinweis: string }
type Stand = { bereit: boolean; punkte: Punkt[]; betriebe: string[] }

/** Ist alles bereit zum Umschalten? Liest bei Meta — deshalb nicht im
 *  15-Sekunden-Takt, sondern beim Öffnen und auf Knopfdruck. */
function Einrichtung() {
  const q = useQuery<Stand>({
    queryKey: ["whatsapp-status"],
    queryFn: () => holeJson<Stand>("/api/v1/whatsapp/status"),
    staleTime: 5 * 60_000,
  })
  const aktion = useMutation({
    mutationFn: (pfad: string) => holeJson<Record<string, unknown>>(`/api/v1/whatsapp/${pfad}`, { method: "POST" }),
    onSuccess: () => { toast("Erledigt — Stand wird neu geprüft"); q.refetch() },
    onError: (e: Error) => toast(e.message, "error"),
  })
  const registrieren = useMutation({
    mutationFn: (pin: string) => holeJson<Record<string, unknown>>("/api/v1/whatsapp/assistent-registrieren", {
      method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ pin }),
    }),
    onSuccess: () => { toast("Assistenten-Nummer registriert — Stand wird neu geprüft"); q.refetch() },
    onError: (e: Error) => toast(e.message, "error"),
  })
  const d = q.data
  if (!d) return null
  const speicherOffen = d.punkte.filter((p) => p.schluessel.startsWith("speicherort") && p.ok !== true
    && !p.wert.startsWith("unbekannt"))
  const assistentFehlt = d.punkte.some((p) => p.schluessel === "assistent" && p.ok === false)
  const offen = d.punkte.filter((p) => p.ok !== true).length
  // Nur aufgeklappt, wenn etwas KAPUTT ist (❌) — Warten (⏳) und „bereit"
  // bleiben zu (Paul 08.10.2026: „dauerhaft ausgeklappt und nervt").
  const kaputt = d.punkte.some((p) => p.ok === false)
  const vorlagenFehlen = d.punkte.some((p) => p.schluessel.startsWith("vorlage_") && p.ok === false)
  const webhookFehlt = d.punkte.some((p) => p.schluessel === "webhook" && p.ok === false)
  return (
    <details className="pp-card" style={{ padding: "8px 14px", marginBottom: 12, fontSize: 13 }} data-testid="wa-einrichtung"
      open={kaputt}>
      <summary style={{ cursor: "pointer" }}>
        <strong>Einrichtung:</strong>{" "}
        {d.bereit ? "bereit ✓" : `${offen} Punkt${offen === 1 ? "" : "e"} offen`}
        {" · "}Betriebe auf WhatsApp: {d.betriebe.length ? d.betriebe.join(", ") : "keiner"}
      </summary>
      <table style={{ marginTop: 8, fontSize: 12, borderCollapse: "collapse" }}>
        <tbody>
          {d.punkte.map((p) => (
            <tr key={p.schluessel} data-testid={`wa-punkt-${p.schluessel}`}>
              <td style={{ padding: "2px 8px 2px 0" }}>{p.ok === true ? "✅" : p.ok === null ? "⏳" : "❌"}</td>
              <td style={{ padding: "2px 12px 2px 0" }}>{p.label}</td>
              <td style={{ padding: "2px 12px 2px 0", color: "#5f5e5a" }}>{p.wert}</td>
              <td style={{ padding: "2px 0", color: "#888780" }}>{p.hinweis}</td>
            </tr>
          ))}
        </tbody>
      </table>
      <div style={{ display: "flex", gap: 8, marginTop: 8 }}>
        <button className="pp-btn pp-btn-sm" onClick={() => q.refetch()} disabled={q.isFetching}>
          {q.isFetching ? "Prüft …" : "Neu prüfen"}
        </button>
        {vorlagenFehlen && (
          <button className="pp-btn pp-btn-sm pp-btn-primary" data-testid="wa-vorlagen-einreichen"
            disabled={aktion.isPending} onClick={() => aktion.mutate("vorlagen-einreichen")}>Vorlagen einreichen</button>
        )}
        {webhookFehlt && (
          <button className="pp-btn pp-btn-sm pp-btn-primary" data-testid="wa-webhook-anmelden"
            disabled={aktion.isPending} onClick={() => aktion.mutate("webhook-anmelden")}>Webhook anmelden</button>
        )}
        {assistentFehlt && (
          <button className="pp-btn pp-btn-sm pp-btn-primary" data-testid="wa-assistent-registrieren"
            disabled={registrieren.isPending}
            onClick={() => {
              const pin = (window.prompt("Sechsstellige PIN für die Assistenten-Nummer (Zwei-Schritt-PIN bei Meta, frei wählbar — bitte merken):") || "").trim()
              if (/^[0-9]{6}$/.test(pin)) registrieren.mutate(pin)
              else if (pin) toast("Die PIN braucht genau sechs Ziffern", "error")
            }}>Assistenten-Nummer registrieren</button>
        )}
      </div>
      {speicherOffen.length > 0 && <SpeicherortEu offen={speicherOffen} onFertig={() => q.refetch()} />}
      <NummerAnmelden mitAssistent={d.punkte.some((p) => p.schluessel === "assistent")} onFertig={() => q.refetch()} />
    </details>
  )
}

/** Meta „Local Storage“: Nachrichten in der EU (Deutschland) speichern
 *  (Paul 21.10.2026, Datenschutz). Die Nummer wird dafür kurz ab- und wieder
 *  angemeldet — mit der Zwei-Schritt-PIN, die nur Paul kennt. */
function SpeicherortEu({ offen, onFertig }: { offen: Punkt[]; onFertig: () => void }) {
  const wahl = offen.map((p) => (p.schluessel === "speicherort_assistent" ? "assistent" : "haupt"))
  const [nummer, setNummer] = useState(wahl[0])
  const [pin, setPin] = useState("")
  const umstellen = useMutation({
    mutationFn: () => holeJson<Record<string, unknown>>("/api/v1/whatsapp/speicherort-eu", {
      method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ nummer, pin }),
    }),
    onSuccess: () => { setPin(""); toast("Speicherort auf EU (Deutschland) umgestellt"); onFertig() },
    onError: (e: Error) => { setPin(""); toast(e.message, "error"); onFertig() },
  })
  const name = nummer === "assistent" ? "Assistenten-Nummer" : "Hauptnummer"
  return (
    <div style={{ display: "flex", gap: 8, marginTop: 8, alignItems: "center", flexWrap: "wrap" }} data-testid="wa-speicherort">
      <span>Speicherort auf EU (Deutschland):</span>
      {wahl.length > 1 && (
        <select className="pp-input" value={nummer} onChange={(e) => setNummer(e.target.value)} data-testid="wa-speicherort-nummer">
          {wahl.map((w) => <option key={w} value={w}>{w === "assistent" ? "Assistenten-Nummer" : "Hauptnummer"}</option>)}
        </select>
      )}
      <input className="pp-input" type="password" inputMode="numeric" autoComplete="off" maxLength={6}
        placeholder="PIN (6 Ziffern)" value={pin} onChange={(e) => setPin(e.target.value.replace(/\D/g, ""))}
        style={{ width: 130 }} data-testid="wa-speicherort-pin" />
      <button className="pp-btn pp-btn-sm pp-btn-primary" data-testid="wa-speicherort-umstellen"
        disabled={umstellen.isPending || pin.length !== 6}
        onClick={async () => {
          const ja = await nachfragen({
            titel: `${name} jetzt umstellen?`,
            zeilen: [
              "Die Nummer wird bei Meta ab- und mit Speicherort Deutschland wieder angemeldet.",
              "In der Zeit (laut Meta unter 5 Minuten) kommen keine WhatsApp-Nachrichten an und gehen keine raus.",
              "Bei falscher PIN bleibt die Nummer abgemeldet, bis sie mit der richtigen PIN erneut angemeldet wird.",
              "Meta erlaubt höchstens 10 An-/Abmeldungen je Nummer in 72 Stunden.",
            ],
            ja: "Jetzt umstellen",
          })
          if (ja) umstellen.mutate()
        }}>{umstellen.isPending ? "Stellt um …" : "Umstellen"}</button>
    </div>
  )
}

/** NOTWEG (21.10.2026): Nummer wieder anmelden, Speicherort zurück auf
 *  Meta-Standard. Für den Fall, dass eine Nummer abgemeldet ist. */
function NummerAnmelden({ mitAssistent, onFertig }: { mitAssistent: boolean; onFertig: () => void }) {
  const [nummer, setNummer] = useState("haupt")
  const [pin, setPin] = useState("")
  const anmelden = useMutation({
    mutationFn: () => holeJson<Record<string, unknown>>("/api/v1/whatsapp/nummer-anmelden", {
      method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ nummer, pin }),
    }),
    onSuccess: () => { setPin(""); toast("Nummer wieder angemeldet"); onFertig() },
    onError: (e: Error) => { setPin(""); toast(e.message, "error"); onFertig() },
  })
  return (
    <div style={{ display: "flex", gap: 8, marginTop: 8, alignItems: "center", flexWrap: "wrap" }} data-testid="wa-notweg">
      <span>Notweg – Nummer wieder anmelden (ohne EU-Speicherort):</span>
      {mitAssistent && (
        <select className="pp-input" value={nummer} onChange={(e) => setNummer(e.target.value)} data-testid="wa-notweg-nummer">
          <option value="haupt">Hauptnummer</option>
          <option value="assistent">Assistenten-Nummer</option>
        </select>
      )}
      <input className="pp-input" type="password" inputMode="numeric" autoComplete="off" maxLength={6}
        placeholder="PIN (6 Ziffern)" value={pin} onChange={(e) => setPin(e.target.value.replace(/\D/g, ""))}
        style={{ width: 130 }} data-testid="wa-notweg-pin" />
      <button className="pp-btn pp-btn-sm pp-btn-primary" data-testid="wa-notweg-anmelden"
        disabled={anmelden.isPending || pin.length !== 6} onClick={() => anmelden.mutate()}>
        {anmelden.isPending ? "Meldet an …" : "Anmelden"}</button>
    </div>
  )
}

type AssistentZeile = {
  tenant: string; nachrichten: number; sprach: number; nutzer: number; zuletzt: string | null
  nicht_verstanden: number; quote_nicht_verstanden: number; rueckfragen: number
  angebote: number; angebote_in_hero: number; berichte: number; berichte_in_hero: number
  aufgaben_angelegt: number; aufgaben_geaendert: number; projekte_angelegt: number; fotos: number
}
type AssistentStand = { tage: number; betriebe: AssistentZeile[]; abbrueche: { tenant: string; endung: string; zeit: string }[] }

/** Wie läuft der WhatsApp-Assistent? Nur Zahlen — wo Gespräche hängen bleiben, sieht man
 *  ohne Screenshots (Paul 12.10.2026). Zugeklappt; lädt beim Öffnen der Seite einmal. */
function AssistentAuswertung() {
  const q = useQuery<AssistentStand>({
    queryKey: ["whatsapp-assistent-auswertung"],
    queryFn: () => holeJson<AssistentStand>("/api/v1/whatsapp/assistent/auswertung?tage=14"),
    staleTime: 5 * 60_000,
  })
  const d = q.data
  if (!d || !d.betriebe?.length) return null
  const abbrueche = d.abbrueche ?? []
  const zelle = { padding: "2px 10px 2px 0", textAlign: "right" as const }
  return (
    <details className="pp-card" style={{ padding: "8px 14px", marginBottom: 12, fontSize: 13 }} data-testid="wa-assistent-auswertung">
      <summary style={{ cursor: "pointer" }}>
        <strong>Assistent ({d.tage} Tage):</strong>{" "}
        {d.betriebe.reduce((s, b) => s + b.nachrichten, 0)} Nachrichten ·{" "}
        {d.betriebe.reduce((s, b) => s + b.nicht_verstanden, 0)} nicht verstanden ·{" "}
        {d.betriebe.reduce((s, b) => s + b.angebote_in_hero, 0)} Angebote in HERO
      </summary>
      <table style={{ marginTop: 8, fontSize: 12, borderCollapse: "collapse" }}>
        <thead>
          <tr style={{ color: "#888780" }}>
            <th style={{ ...zelle, textAlign: "left" }}>Betrieb</th><th style={zelle}>Nachrichten</th><th style={zelle}>davon Sprache</th>
            <th style={zelle}>nicht verstanden</th><th style={zelle}>Angebote → HERO</th><th style={zelle}>Berichte → HERO</th>
            <th style={zelle}>Aufgaben</th><th style={zelle}>Projekte</th><th style={zelle}>Fotos</th>
          </tr>
        </thead>
        <tbody>
          {d.betriebe.map((b) => (
            <tr key={b.tenant} data-testid={`wa-assistent-${b.tenant}`}>
              <td style={{ ...zelle, textAlign: "left" }}>{b.tenant}</td>
              <td style={zelle}>{b.nachrichten}</td><td style={zelle}>{b.sprach}</td>
              <td style={{ ...zelle, color: b.quote_nicht_verstanden >= 15 ? "#b3261e" : undefined }}>
                {b.nicht_verstanden} ({b.quote_nicht_verstanden} %)
              </td>
              <td style={zelle}>{b.angebote} → {b.angebote_in_hero}</td>
              <td style={zelle}>{b.berichte} → {b.berichte_in_hero}</td>
              <td style={zelle}>{b.aufgaben_angelegt} neu, {b.aufgaben_geaendert} geändert</td>
              <td style={zelle}>{b.projekte_angelegt}</td><td style={zelle}>{b.fotos}</td>
            </tr>
          ))}
        </tbody>
      </table>
      {abbrueche.length > 0 && (
        <div style={{ marginTop: 8, color: "#5f5e5a" }} data-testid="wa-assistent-abbrueche">
          Zuletzt hängen geblieben:{" "}
          {abbrueche.map((a) => `${a.tenant} …${a.endung} (${parseServerDate(a.zeit)?.toLocaleString("de-DE", { day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit" }) ?? ""})`).join(" · ")}
        </div>
      )}
    </details>
  )
}

export default function WhatsAppPage() {
  const [tenant, setTenant] = useTenantFilter()
  const [aktiv, setAktiv] = useState<string | null>(null)
  const [art, setArt] = useState<"" | "kunde" | "intern">("")
  const [inhaberDialog, setInhaberDialog] = useState(false)
  const qc = useQueryClient()
  const liste = useQuery<{ unterhaltungen: Unterhaltung[]; eingerichtet: boolean }>({
    queryKey: ["whatsapp-unterhaltungen", tenant, art],
    queryFn: () => {
      const p = new URLSearchParams()
      if (tenant) p.set("tenant", tenant)
      if (art) p.set("art", art)
      return holeJson(`/api/v1/whatsapp/unterhaltungen${p.toString() ? `?${p}` : ""}`)
    },
    refetchInterval: 15_000,
  })
  const tenants = useQuery<{ items: { slug: string; name: string }[] }>({
    queryKey: ["whatsapp-betriebe"],
    queryFn: () => holeJson("/api/v1/tenants"),
    staleTime: 1000 * 60 * 5,
  })
  const namen = useMemo(() => Object.fromEntries((tenants.data?.items || []).map((t) => [t.slug, t.name])), [tenants.data])
  const items = liste.data?.unterhaltungen || []
  const gewaehlt = items.find((u) => `${u.telefon}|${u.tenant_slug}` === aktiv) || null

  return (
    <div data-testid="whatsapp-seite">
      <div className="pp-page-header"><h1>WhatsApp</h1></div>
      <FilterBar tenant={tenant} onTenantChange={setTenant}>
        <span className="filter-label" style={{ marginLeft: 12 }}>Von:</span>
        <select value={art} onChange={(e) => setArt(e.target.value as "" | "kunde" | "intern")} style={{ width: "auto" }}
          data-testid="wa-art-filter">
          <option value="">Alle</option>
          <option value="kunde">Kunden</option>
          <option value="intern">Inhaber/Team</option>
        </select>
      </FilterBar>
      <Einrichtung />
      <AssistentAuswertung />
      <div style={{ display: "flex", justifyContent: "flex-end", marginBottom: 8 }}>
        <button className="pp-btn pp-btn-sm pp-btn-primary" onClick={() => setInhaberDialog(true)} data-testid="wa-inhaber-knopf">
          Inhaber / Team anschreiben
        </button>
      </div>
      {inhaberDialog && (
        <InhaberAnschreiben onClose={() => setInhaberDialog(false)} onGesendet={(telefon, t) => {
          setInhaberDialog(false)
          qc.invalidateQueries({ queryKey: ["whatsapp-unterhaltungen"] })
          setAktiv(`${telefon}|${t}`)
        }} />
      )}
      {liste.data && !liste.data.eingerichtet && (
        <div className="pp-card" style={{ padding: 12, marginBottom: 12, fontSize: 13 }}>
          WhatsApp ist in dieser Umgebung noch nicht eingerichtet — es werden nur bisherige Nachrichten angezeigt.
        </div>
      )}
      <div className="review-layout" style={{ gap: 14, height: "calc(100vh - 190px)", minHeight: 500 }}>
        <div className="pp-card" style={{ padding: 0, display: "flex", flexDirection: "column", overflow: "hidden" }}>
          <div style={{ padding: "10px 14px", fontSize: 12, textTransform: "uppercase", letterSpacing: 0.4, color: "#888780",
            borderBottom: "0.5px solid rgba(44,44,42,0.08)" }}>
            Unterhaltungen ({items.length})
          </div>
          <Liste items={items} aktiv={aktiv} onWahl={(u) => setAktiv(`${u.telefon}|${u.tenant_slug}`)} namen={namen} />
        </div>
        <div style={{ minHeight: 0, overflow: "hidden" }}>
          {gewaehlt ? <Chat key={aktiv!} u={gewaehlt} namen={namen} /> : (
            <div className="pp-card" style={{ padding: 48, textAlign: "center", color: "var(--text-muted)" }}>
              Unterhaltung links auswählen.
            </div>
          )}
        </div>
      </div>
    </div>
  )
}
